import { NAV_BORDER_THRESHOLD } from '@/components/common/pageLayout/PageLayout';
import { linkHoverStyleProps } from '@/components/common/styleHelpers';
import { Text } from '@/components/common/Typography';
import { signOut } from '@/services/stytch';
import { Box } from '@chakra-ui/react';
import type { CurrentUserContext } from '@common/react/user';
import { useCurrentUser } from '@common/react/user';
import { useShowReferrals } from '@common/react/useShowReferrals';
import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import { NextRouter } from 'next/router';
import { FlexCol } from '../common/Flex';
import { ArrowLeft } from '../common/icons';
import SidebarNav, { SidebarNavPageItem } from '../common/SidebarNav';

export const buildSettingsNavItems = ({
    currentUser,
    pathname,
    showReferrals,
}: {
    currentUser: CurrentUserContext;
    pathname: string;
    showReferrals: boolean;
}): SidebarNavPageItem[] => {
    const { featureFlags } = currentUser;

    return [
        {
            href: '/profile',
            text: 'Your profile',
        },
        {
            href: '/sharing',
            text: 'Sharing',
        },
        {
            href: '/referrals',
            text: 'Invite friends & earn',
            isHidden: !showReferrals || featureFlags.isShutdownV1Enabled,
        },
        {
            href: '/accounts',
            text: 'Accounts',
        },
        {
            href: '/membership',
            text: 'Membership',
        },
        {
            href: '/notifications',
            text: 'Notifications',
        },
        {
            href: '/documents',
            text: 'Documents',
        },
        {
            href: 'https://helpcenter.withplenty.com/hc/en-us',
            target: '_blank',
            text: 'Help center',
        },
        {
            href: '/admin',
            text: 'Admin',
            isHidden: !currentUser.userInfo.isAdmin,
            isAdmin: true,
        },
        {
            href: '/styleguide/typography',
            text: 'Styleguide',
            isHidden: !currentUser.userInfo.isAdmin,
            isAdmin: true,
        },
        {
            onClick: ({ router }: { router: NextRouter }) => {
                // Tack on a query param to ensure the resulting signin page
                // doesn't encode this URL as a `from` param and redirect to it.
                router.replace({
                    query: { ...router.query, signOut: 'true' },
                });
                signOut();
            },
            text: 'Sign out',
        },
    ]
        .filter((item) => !item.isHidden)
        .map((item) => ({
            ...item,
            isSelected:
                'href' in item && item.href
                    ? pathname.includes(item.href)
                    : false,
        }));
};

const SETTINGS_SUBPATHS = ['/admin/rules'];

export const isSettingsPath = (
    currentUser: CurrentUserContext,
    pathname: string,
    showReferrals: boolean
): boolean =>
    buildSettingsNavItems({
        currentUser,
        pathname,
        showReferrals,
    })
        .map((item) => ('href' in item ? item.href : ''))
        .includes(pathname) || SETTINGS_SUBPATHS.includes(pathname);

const SettingsSidebar = () => {
    const pathname = usePathname();
    const currentUser = useCurrentUser();
    const showReferrals = useShowReferrals();
    const navItems = buildSettingsNavItems({
        currentUser,
        pathname,
        showReferrals: showReferrals.enabled,
    });

    return (
        <FlexCol
            minWidth={260}
            gap={2}
            position="sticky"
            top={NAV_BORDER_THRESHOLD + 8}
            height="100%"
        >
            <Box {...linkHoverStyleProps()} mb={6}>
                <NextLink href="/">
                    <ArrowLeft size="sm" mr={1} />
                    <Text fontWeight={500}>Back to dashboard</Text>
                </NextLink>
            </Box>

            <Text mt={6} mb={4} textTransform="uppercase" fontSize="xs">
                Settings
            </Text>

            <SidebarNav items={navItems} />
        </FlexCol>
    );
};

export default SettingsSidebar;
